// ── El monto de una oportunidad ──────────────────────────────────────────────
// Se escribe a mano, y de arranque se ofrece lo que ese contacto tiene
// cotizado: sumando TODAS sus cotizaciones vivas, no la última. Un cliente al
// que se le cotizó la web y después el mantenimiento vale las dos cosas, y
// poner una sola subestima el embudo.
//
// La sugerencia nunca pisa lo escrito. Si alguien puso un número a mano, sabe
// algo que la suma no —un descuento hablado, una parte que no se va a cerrar—
// y el programa no tiene con qué discutirlo.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { formatMoney } from '@evo-hub/shared';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Pressable } from '../ui/Pressable.js';
import { api } from '../../lib/api.js';

export interface ValorDeLaCartaProps {
  opportunityId: number;
  valueCents: number;
  currency: string;
  onGuardar: (valueCents: number) => void;
  guardando?: boolean;
}

/** Lo que se escribe es dinero de verdad, no centavos: 1.500 son 150000. */
function aCentavos(texto: string): number | null {
  const limpio = texto.replace(/[^\d.,-]/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.');
  const n = Number(limpio);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export function ValorDeLaCarta({ opportunityId, valueCents, currency, onGuardar, guardando = false }: ValorDeLaCartaProps) {
  const { t } = useTranslation();
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');

  const cotizado = useQuery({
    queryKey: ['opportunity-cotizado', opportunityId],
    queryFn: () => api.get<{ totalCents: number; cotizaciones: number }>(`/api/opportunities/${opportunityId}/cotizado`),
  });

  useEffect(() => {
    if (editando) setTexto((valueCents / 100).toString());
  }, [editando, valueCents]);

  const total = cotizado.data?.totalCents ?? 0;
  const cuantas = cotizado.data?.cotizaciones ?? 0;
  // Solo se ofrece si dice algo distinto de lo que ya está.
  const sugerencia = total > 0 && total !== valueCents ? total : null;

  function guardar() {
    const centavos = aCentavos(texto);
    if (centavos == null) { setEditando(false); return; }
    if (centavos !== valueCents) onGuardar(centavos);
    setEditando(false);
  }

  if (editando) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
        <Input
          value={texto}
          onChange={setTexto}
          autoFocus
          onBlur={guardar}
          disabled={guardando}
        />
        <Button variant="ghost" tone="muted" size="sm" onClick={() => setEditando(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--evo-space-3)', flexWrap: 'wrap' }}>
      <Pressable onPress={() => setEditando(true)} ariaLabel={t('opportunities.editValue')}>
        <span style={{
          fontSize: 'var(--evo-fs-h2)', color: 'var(--evo-fg-1)',
          fontVariantNumeric: 'tabular-nums', lineHeight: 1,
        }}>
          {formatMoney(valueCents, currency)}
        </span>
      </Pressable>

      {sugerencia != null && (
        <Pressable onPress={() => onGuardar(sugerencia)} ariaLabel={t('opportunities.useQuoted')}>
          <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-cyan)' }}>
            {t('opportunities.quotedTotal', {
              amount: formatMoney(sugerencia, currency),
              count: cuantas,
            })}
          </span>
        </Pressable>
      )}
    </div>
  );
}
